import { useEffect } from 'react';
import { useOnsiteCampaignUrlParams } from './useOnsiteCampaignUrlParams';
import { useCampaignEditorContext } from '../pages/CampaignEditor/contexts/CampaignEditorContext';
import { useCampaignSectionContext } from '../context/CampaignSectionContext';
import { useWorkflowForm } from '../pages/CampaignEditor/tabs/CampaignWorkflowTab/contexts/CampaignWorkflowFormContext';
import { CampaignResponseToStateAdapter } from '../pages/CampaignEditor/infrastructure/Adapters/CampaignResponseToStateAdapter/CampaignResponseToStateAdapter';
import { useCampaignQuery } from '../pages/CampaignEditor/infrastructure/Queries/useCampaignQuery';
import { useTemplateQuery } from '../pages/CampaignEditor/infrastructure/Queries/useTemplateQuery';
import { useSubjectLinesQuery } from '../pages/CampaignEditor/infrastructure/Queries/useSubjectLinesQuery';
import { useWorkFlowCanvas } from '../pages/CampaignEditor/tabs/CampaignWorkflowTab/contexts/CampaignFlowProvider';
import {
  CampaignResponseToCanvasStateAdapter,
  CampaignResponseModel,
} from '../pages/CampaignEditor/infrastructure/Adapters/CampaignResponseToCanvasStateAdapter/CampaignResponseToCanvasStateAdapter';
import { NewCampaignResponseToCanvasStateAdapter } from '../pages/CampaignEditor/infrastructure/Adapters/NewCampaignResponseToCanvasStateAdapter';
import { useDrawer } from '../pages/CampaignEditor/tabs/CampaignWorkflowTab/state/Drawer/hooks/useDrawer';
import { NodeTypeKeys } from '../pages/CampaignEditor/domain/Canvas/utils/NodeTypeKeys';
import { uuid } from 'utils';
import { useTranslation } from 'react-i18next';

export function useGetOnsiteCampaignQuery(siteId: string, campaignId: string) {
  const { state, dispatch } = useCampaignSectionContext();
  const { dispatch: editorDispatch } = useCampaignEditorContext();
  const { dispatch: canvasDispatch } = useWorkFlowCanvas();
  const { actions } = useWorkflowForm();
  const { openDrawer } = useDrawer();

  const campaignQuery = useCampaignQuery(siteId, campaignId);
  const templateQuery = useTemplateQuery(siteId);
  const subjectLinesQuery = useSubjectLinesQuery(siteId);
  const { i18n } = useTranslation('campaigns');

  useEffect(() => {
    if (campaignId !== state.selectedCampaign.id) {
      dispatch({ type: 'CLEAR_SELECTED_CAMPAIGN' });
      editorDispatch({ type: 'CLEAR_SELECTED_CAMPAIGN' });
    }

    if (campaignId === 'new') {
      openDrawer(NodeTypeKeys.triggerNodeType, 'trigger');
      actions.setFocused('trigger', true);

      canvasDispatch({
        type: 'INIT',
        payload: new NewCampaignResponseToCanvasStateAdapter(),
      });
    } else if (!campaignQuery.isLoading && !templateQuery.isLoading) {
      dispatch({ type: 'SET_SELECTED_CAMPAIGN', payload: { selectedCampaign: campaignQuery.data } });

      const model: CampaignResponseModel = {
        ...campaignQuery.data,
        onsiteMessageSegments: campaignQuery.data.onsiteMessageSegments?.map(s => ({
          ...s,
          branchId: uuid(),
        })),
      };

      editorDispatch({
        type: 'SET_CAMPAIGN_EDITOR_INITIAL_STATE',
        payload: {
          initialState: new CampaignResponseToStateAdapter(
            model,
            templateQuery.data,
            subjectLinesQuery.data,
            i18n.language,
          ),
        },
      });

      canvasDispatch({
        type: 'INIT',
        payload: new CampaignResponseToCanvasStateAdapter(model),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, campaignQuery.isLoading, campaignQuery.data, templateQuery.isLoading, state.selectedCampaign.id]);

  return [campaignQuery, templateQuery];
}

/**
 * Sets the initial state based on a given campaign.
 */
export function useOnsiteCampaignInitialState() {
  const { siteId, campaignId } = useOnsiteCampaignUrlParams();
  const [campaignQuery, templateQuery] = useGetOnsiteCampaignQuery(siteId, campaignId);
  return { campaignQuery, templateQuery };
}
